import * as Enums from "../enums/index.js";
import RankingStatCalculatorSelector from "../selectors/RankingStatCalculatorSelector.js";
import EnumValueProvider from "../providers/EnumValueProvider.js";
import ResultsSlice from "../valueObjects/ResultsSlice.js";
import RankingStatCalculatorBase from "../calculators/rankingStatsCalculators/RankingStatCalculatorBase.js";

class RankingStatsGenerator {
    constructor(
        pitchingStatsSelector, battingStatsSelector, generalStatsSelector,
        pitchingStats, battingStats, generalStats
    ) {
        if (!(pitchingStatsSelector instanceof RankingStatCalculatorSelector)) {
            throw new TypeError('pitchingStatsSelector must be an instance inheriting from RankingStatCalculatorSelector class');
        }
        this.pitchingStatsSelector = pitchingStatsSelector;

        if (!(battingStatsSelector instanceof RankingStatCalculatorSelector)) {
            throw new TypeError('battingStatsSelector must be an instance inheriting from RankingStatCalculatorSelector class');
        }
        this.battingStatsSelector = battingStatsSelector;

        if (!(generalStatsSelector instanceof RankingStatCalculatorSelector)) {
            throw new TypeError('generalStatsSelector must be an instance inheriting from RankingStatCalculatorSelector class');
        }
        this.generalStatsSelector = generalStatsSelector;

        if (typeof(pitchingStats) === 'undefined') {
            this.pitchingStats = [
                // radar based
                Enums.PitchingRankingStats.PitchSpeedAvg,
                Enums.PitchingRankingStats.PitchSpeedMax,
                Enums.PitchingRankingStats.PitchSpinAvg,
                Enums.PitchingRankingStats.PitchSpinMax,
                // Enums.PitchingRankingStats.PitchUsage,  // note - percentage values cannot be stored in this way without generating 0 % values for missing pitch types
                Enums.PitchingRankingStats.PitchMovementHAvg,
                Enums.PitchingRankingStats.PitchMovementVAvg,
                Enums.PitchingRankingStats.PitchMovementVIndAvg,
                // batting useful for pitching
                Enums.PitchingRankingStats.ExitSpeedMax,
                Enums.PitchingRankingStats.ExitSpeedAvg,
                Enums.PitchingRankingStats.HitLaunchVAvg,
                Enums.PitchingRankingStats.HitLaunchVMax,
                Enums.PitchingRankingStats.HardHitsAllowed,
                Enums.PitchingRankingStats.HardHitsRateAllowed,
                // cumulative data
                Enums.PitchingRankingStats.AverageFastballVelocity,
                Enums.PitchingRankingStats.MaximumFastballVelocity,
                Enums.PitchingRankingStats.AverageBreakingBallVelocity,
                Enums.PitchingRankingStats.AverageOffSpeedVelocity,
                Enums.PitchingRankingStats.AverageFastballSpin,
                Enums.PitchingRankingStats.AverageBreakingBallSpin,
                Enums.PitchingRankingStats.AverageOffSpeedSpin,
                // other stats
                Enums.PitchingRankingStats.NumberOfPitches,
                 // plate discipline
                Enums.PitchingRankingStats.Swings,  Enums.PitchingRankingStats.Whiffs, Enums.PitchingRankingStats.Contacts,
                Enums.PitchingRankingStats.KZonePitches, Enums.PitchingRankingStats.OZonePitches,
                Enums.PitchingRankingStats.ZContacts, Enums.PitchingRankingStats.ZSwings, Enums.PitchingRankingStats.ZWhiffs,
                Enums.PitchingRankingStats.OContacts, Enums.PitchingRankingStats.OSwings, Enums.PitchingRankingStats.OWhiffs,
                // base hits
                Enums.PitchingRankingStats.HitsAllowed,
                Enums.PitchingRankingStats.HomeRunsAllowed,
                Enums.PitchingRankingStats.BaseHitsAllowed,
                Enums.PitchingRankingStats.TwoBaseHitsAllowed,
                Enums.PitchingRankingStats.ThreeBaseHitsAllowed,
                // strikeouts
                Enums.PitchingRankingStats.Strikeouts,
                // Enums.PitchingRankingStats.StrikeoutRate, // percentage values are troublesome
                // base on balls
                Enums.PitchingRankingStats.WalksAllowed,
                // Enums.PitchingRankingStats.WalkRateAllowed, // percentage values are troublesome
                Enums.PitchingRankingStats.IntentionalWalksAllowed,
                // hit by pitch
                Enums.PitchingRankingStats.HitBatsmen,
                // starting pitcher, relief pitcher based
                Enums.PitchingRankingStats.GamesStarted,
                Enums.PitchingRankingStats.GamesCompleted,
                Enums.PitchingRankingStats.GamesFinished,
                // win / loss, BBH with scoring + new apps
                Enums.PitchingRankingStats.Wins,
                Enums.PitchingRankingStats.Looses,
                // earned runs
                Enums.PitchingRankingStats.EarnedRuns,
                // plate appearances related
                // Enums.PitchingRankingStats.PitchesPerPa, // @see PitchingRatioStatsCalculator
                Enums.PitchingRankingStats.BattersFaced,
                Enums.PitchingRankingStats.TimesOnBaseAgainst,
                Enums.PitchingRankingStats.OnBaseAgainst,  // @see PitchingRatioStatsCalculator
                // batted ball type / batted ball events
                Enums.PitchingRankingStats.GroundBalls,
                Enums.PitchingRankingStats.LineDrives,
                Enums.PitchingRankingStats.FlyBalls,
                Enums.PitchingRankingStats.PopUps,
                // Batted ball events
                Enums.PitchingRankingStats.BattedBalls,
                // At-bat related stiff
                Enums.PitchingRankingStats.AtBats,
                Enums.PitchingRankingStats.BattingAverage, // could be handled by BattingRatioStatsCalculator, but we don't have at-bats for all sessions, when hits are always available
                Enums.PitchingRankingStats.SluggingPercentage, // @see BattingRatioStatsCalculator
                // Enums.PitchingRankingStats.IsolatedPower, // not sure if it makes sesne for pitcher ?
                Enums.PitchingRankingStats.BattingAverageOnBallsInPlay,
                // base running stuff
                Enums.PitchingRankingStats.RunsAllowed,
                Enums.PitchingRankingStats.CaughtStealingAllowed,
                Enums.PitchingRankingStats.StolenBasesAllowed,
                Enums.PitchingRankingStats.WildPitches,
                Enums.PitchingRankingStats.Balks,
                Enums.PitchingRankingStats.Pickoffs,
                // innings pithed and related stuff
                Enums.PitchingRankingStats.OutsPitched,
                Enums.PitchingRankingStats.FieldingIndependentPitching,
                // Enums.PitchingRankingStats.InningsPitched, // TODO - this could be also calculated, but because of rounding it is better to calculate it on the fly, it should always end with .0, .(3) or .(6)
                Enums.PitchingRankingStats.StrikeoutsPerNineInnings,
                Enums.PitchingRankingStats.WalksPerNineInnings,
                Enums.PitchingRankingStats.HomeRunsPerNineInnings,
                Enums.PitchingRankingStats.HitsPerNineInnings,
                Enums.PitchingRankingStats.WalksPlusHitsPerInningPitched,
                Enums.PitchingRankingStats.HomeRunToFlyBallRate,
                // GIDP
                Enums.PitchingRankingStats.GroundIntoDoublePlays,
                // GO/AO - new apps
                Enums.PitchingRankingStats.GroundOuts,
                Enums.PitchingRankingStats.AirOuts,
            ];
        }
        else if (!(pitchingStats instanceof Array)) {
            throw new TypeError('pitchingStats must be an array containing key of PitchingRankingStats');
        }
        else {
            this.pitchingStats = [];
            for (const pitchingStat of pitchingStats) {
                const parameter = EnumValueProvider.getValue(
                    pitchingStat, Enums.PitchingRankingStats
                );

                if (!parameter) {
                    throw new Error(`Couldn't find parameter ${pitchingStat}`);
                }

                this.pitchingStats.push(parameter);
            }
        }

        if (typeof(battingStats) === 'undefined') {
            this.battingStats = [
                Enums.BattingRankingStats.ExitSpeedAvg,
                Enums.BattingRankingStats.ExitSpeedMax,
                Enums.BattingRankingStats.HitLaunchVAvg,
                Enums.BattingRankingStats.HitLaunchVMax,
                Enums.BattingRankingStats.HardHits,
                Enums.BattingRankingStats.HardHitRate,
                //Enums.BattingRankingStats.CarryDistanceAvg,
                // pitching useful for pitching
                Enums.BattingRankingStats.PitchSpeedAvg,
                Enums.BattingRankingStats.PitchSpeedMax,
                Enums.BattingRankingStats.PitchSpinAvg,
                Enums.BattingRankingStats.PitchSpinMax,
                // Enums.BattingRankingStats.PitchUsage, // note - percentage values cannot be stored in this way without generating 0 % values for missing pitch types
                Enums.BattingRankingStats.NumberOfPitches,
                // cumulative data
                Enums.BattingRankingStats.AverageExitVelocity,
                Enums.BattingRankingStats.MaximumExitVelocity,
                // plate discipline
                Enums.BattingRankingStats.Swings, Enums.BattingRankingStats.Whiffs, Enums.BattingRankingStats.Contacts,
                Enums.BattingRankingStats.KZonePitches, Enums.BattingRankingStats.OZonePitches,
                Enums.BattingRankingStats.ZContacts, Enums.BattingRankingStats.ZSwings, Enums.BattingRankingStats.ZWhiffs,
                Enums.BattingRankingStats.OContacts, Enums.BattingRankingStats.OSwings, Enums.BattingRankingStats.OWhiffs,
                // base hits
                Enums.BattingRankingStats.Hits,
                Enums.BattingRankingStats.HomeRuns,
                Enums.BattingRankingStats.BaseHits, Enums.BattingRankingStats.TwoBaseHits, Enums.BattingRankingStats.ThreeBaseHits,
                Enums.BattingRankingStats.TotalBases,
                // strikeouts
                Enums.BattingRankingStats.Strikeouts,
                // Enums.BattingRankingStats.StrikeoutRate, // percentage values are troublesome
                // base on balls
                Enums.BattingRankingStats.Walks,
                // Enums.BattingRankingStats.WalkRate, // percentage values are troublesome
                Enums.BattingRankingStats.IntentionalWalks,
                // hit by pitch
                Enums.BattingRankingStats.HitByPitches,
                // reached on error
                Enums.BattingRankingStats.ReachedOnError,
                // plate appearances related
                // Enums.BattingRankingStats.PitchesPerPa, // @see BattingRatioStatsCalculator
                Enums.BattingRankingStats.PlateAppearances,
                Enums.BattingRankingStats.TimesOnBase,
                Enums.BattingRankingStats.OnBasePercentage, // @see BattingRatioStatsCalculator
                // at bat related stats
                Enums.BattingRankingStats.AtBats,
                Enums.BattingRankingStats.BattingAverage, // could be handled by BattingRatioStatsCalculator, but we don't have at-bats for all sessions, when hits are always available
                Enums.BattingRankingStats.SluggingPercentage, // @see BattingRatioStatsCalculator
                Enums.BattingRankingStats.IsolatedPower,
                Enums.BattingRankingStats.BattingAverageOnBallsInPlay,
                // sacrifice related
                Enums.BattingRankingStats.SacrificeFlies,
                Enums.BattingRankingStats.SacrificeHits,
                // GIDP
                Enums.BattingRankingStats.GroundIntoDoublePlays,
                // base running stuff
                Enums.BattingRankingStats.Runs,
                Enums.BattingRankingStats.CaughtStealing,
                Enums.BattingRankingStats.StolenBases,
                // batted ball type / batted ball events
                Enums.BattingRankingStats.GroundBalls,
                Enums.BattingRankingStats.LineDrives,
                Enums.BattingRankingStats.FlyBalls,
                Enums.BattingRankingStats.PopUps,
                // Batted ball events
                Enums.BattingRankingStats.BattedBalls,
                // RBI
                Enums.BattingRankingStats.RunsBattedIn,
                // GO/AO - new apps
                Enums.BattingRankingStats.GroundOuts,
                Enums.BattingRankingStats.AirOuts,
            ];
        }
        else if (!(battingStats instanceof Array)) {
            throw new TypeError('battingStats must be an array containing key of BattingRankingStats');
        }
        else {
            this.battingStats = [];
            for (const battingStat of battingStats) {
                const parameter = EnumValueProvider.getValue(
                    battingStat, Enums.BattingRankingStats
                );

                if (!parameter) {
                    throw new Error(`Couldn't find parameter ${battingStat}`);
                }

                this.battingStats.push(parameter);
            }
        }

        if (typeof(generalStats) === 'undefined') {
            this.generalStats = [
                Enums.GeneralRankingStats.GamesPlayed
            ];
        }
        else if (!(generalStats instanceof Array)) {
            throw new TypeError('generalStats must be an array containing key of GeneralRankingStats enum');
        }
        else {
            this.generalStats = [];
            for (const generalStat of generalStats) {
                const parameter = EnumValueProvider.getValue(
                    generalStat, Enums.GeneralRankingStats
                );

                if (!parameter) {
                    throw new Error(`Couldn't find parameter ${generalStat}`);
                }

                this.generalStats.push(parameter);
            }
        }

        this.terminateGenerationOnError = false;
    }

    * generate(slice, session, league, errors) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        if (typeof(errors) !== 'undefined' && !(errors instanceof Array)) {
            throw new TypeError('errors must be an array or undefined');
        }

        if (!slice.getSmallestSlice()) {
            // no data, continue ??
            return;
        }

        for (const pitchingStat of this.pitchingStats) {
            const calculator = this.pitchingStatsSelector.selectAny(
                pitchingStat, slice, session, league
            );
            if (!calculator) {
                // TODO - log ?
                continue;
            }

            if (!(calculator instanceof RankingStatCalculatorBase)) {
                throw new TypeError(`calculator for ${pitchingStat.key} should be of type RankingStatCalculatorBase`)
            }

            // TODO - its called twice...
            // if (calculator.canCalculate(pitchingStat, slice, league, session)) {
                yield this.tryCalculateStats(
                    calculator, pitchingStat, slice, session, league, errors
                );
            //}
        }

        for (const battingStat of this.battingStats) {
            const calculator = this.battingStatsSelector.selectAny(
                battingStat, slice, session, league
            );
            if (!calculator) {
                // TODO - log ?
                continue;
            }

            if (!(calculator instanceof RankingStatCalculatorBase)) {
                throw new TypeError(`calculator for ${battingStat.key} should be of type RankingStatCalculatorBase`)
            }

           // if (calculator.canCalculate(battingStat, slice, league, session)) {
                yield this.tryCalculateStats(
                    calculator, battingStat, slice, session, league, errors
                );
            // }
        }

        for (const generalStat of this.generalStats) {
            const calculator = this.generalStatsSelector.selectAny(
                generalStat, slice, session, league
            );
            if (!calculator) {
                // TODO - log ?
                continue;
            }

            if (!(calculator instanceof RankingStatCalculatorBase)) {
                throw new TypeError(`calculator for ${generalStat.key} should be of type RankingStatCalculatorBase`)
            }

            // if (calculator.canCalculate(generalStat, slice, league, session)) {
                yield this.tryCalculateStats(
                    calculator, generalStat, slice, session, league, errors
                );
            // }
        }
    }

    tryCalculateStats (calculator, stat, slice, session, league, errors) {
        let calculated = null;
        try {
            calculated = calculator.calculate(
                stat, slice, session, league
            );
        } catch (error) {
            if (errors instanceof Array) {
                errors.push({
                    error,
                    stat,
                    calculator: calculator
                });
            }

            if (this.terminateGenerationOnError) {
                throw error;
            }
        }

        return calculated;
    }
}

export default RankingStatsGenerator;